import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0E11] blueprint-grid grain flex items-center justify-center px-6" data-testid="login-page">
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[420px] h-[420px] rounded-full bg-amber-500/5 blur-[120px] pointer-events-none" />
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md relative"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#6B7280] hover:text-amber-500 transition-colors mb-8" data-testid="back-to-site-link">
          <ArrowLeft size={15} /> Back to site
        </Link>
        <div className="bg-[#151821] border border-white/10 rounded-2xl p-9">
          <Logo />
          <div className="mt-8 flex items-center gap-2 font-mono-tactical text-[11px] tracking-[0.25em] text-amber-500">
            <ShieldCheck size={14} /> OUTREACH COMMAND CENTER
          </div>
          <h1 className="mt-3 font-display font-bold text-2xl text-[#F3F4F6] tracking-tight">Owner sign-in</h1>
          <p className="mt-2 text-sm text-[#9CA3AF]">Pipeline, inquiries, and site copy — for Angel only.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">EMAIL</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-4 py-3 text-sm text-[#F3F4F6] outline-none transition-colors"
                placeholder="admin@street2strategy.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label className="block font-mono-tactical text-[11px] tracking-[0.2em] text-[#6B7280] mb-2">PASSWORD</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-[#0D0E11] border border-white/10 focus:border-amber-500/60 rounded-lg px-4 py-3 text-sm text-[#F3F4F6] outline-none transition-colors"
                placeholder="••••••••••"
                data-testid="login-password-input"
              />
            </div>
            {error && (
              <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3" data-testid="login-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-[#0D0E11] font-bold px-6 py-3.5 rounded-full transition-colors"
              data-testid="login-submit-btn"
            >
              {loading ? <Loader2 size={17} className="animate-spin" /> : null}
              {loading ? "Signing in…" : "Enter Command Center"}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
}
